import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import blogArticles from "../data/blogArticles";
import SearchBar from "../components/blog/SearchBar";
import CategoryFilter from "../components/blog/CategoryFilter";
import BlogCard from "../components/blog/BlogCard";
import GridOverlay from "../components/ui/GridOverlay";
import ArticleContainer from "../components/blog/ArticleContainer";

const Blog = () => {
    const [articles, setArticles] = useState([]);
    const [filteredArticles, setFilteredArticles] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("Tous");
    const [prevArticleIds, setPrevArticleIds] = useState(new Set());
    const [isChangingFilter, setIsChangingFilter] = useState(false);
    const queryParams = new URLSearchParams(location.search);
    

    const categories = [
        "Tous",
        ...new Set(blogArticles.map((article) => article.category)),
    ];

    useEffect(() => {
        setArticles(blogArticles);
        setFilteredArticles(blogArticles);
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        // Mettre à jour la catégorie sélectionnée si l'URL change
        const categoryFromUrl = queryParams.get("category");
        if (
            categoryFromUrl &&
            categories.includes(categoryFromUrl) &&
            selectedCategory !== categoryFromUrl
        ) {
            setSelectedCategory(categoryFromUrl);
        }
    }, [location.search]);

    useEffect(() => {
        let results = articles;

        if (searchTerm) {
            results = results.filter(
                (article) =>
                    article.title
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    article.summary
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
            );
        }

        if (selectedCategory !== "Tous") {
            results = results.filter(
                (article) => article.category === selectedCategory
            );
        }

        // Méthode améliorée pour les transitions
        document.documentElement.style.overflowX = "hidden";
        const bodyWidth = document.body.clientWidth;
        document.body.style.width = `${bodyWidth}px`;

        setIsChangingFilter(true);
        setPrevArticleIds(
            new Set(filteredArticles.map((article) => article.id))
        );

        setTimeout(() => {
            setFilteredArticles(results);

            setTimeout(() => {
                setIsChangingFilter(false);
                document.body.style.width = "";
            }, 150);
        }, 50);
    }, [searchTerm, selectedCategory, articles]);

    const handleSearch = (term) => {
        setSearchTerm(term);
    };

    const handleFilterChange = (category) => {
        if (category === selectedCategory) return;
        setSelectedCategory(category);
    };

    const isNewArticle = (articleId) => {
        return !prevArticleIds.has(articleId);
    };

    return (
        <main
            className={`min-h-screen pt-[100px] relative ${
                isChangingFilter ? "overflow-hidden" : ""
            }`}
        >
            <Helmet>
                <title>
                    Blog - Expertise Web, Mobile, Design & SEO
                </title>
                <meta
                    name="description"
                    content="Découvrez nos articles experts sur le développement web, mobile, le design et le SEO. Conseils, tendances et astuces pour améliorer votre présence en ligne."
                />
                <meta
                    name="keywords"
                    content="blog agence web, développement web, applications mobiles, design UI/UX, SEO, référencement, React, React Native"
                />
                <link rel="canonical" href="https://www.nyloria.fr/blog" />
            </Helmet>

            <GridOverlay />

            <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-12 md:py-20 relative z-10">
                <div className="mb-16 max-w-3xl mx-auto text-center animate-fadeIn">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight text-gradient pb-6 animate-slideUp title">
                        Blog & Ressources
                    </h1>
                    <p className="text-base md:text-xl text-gray-600 mx-auto max-w-2xl leading-relaxed animate-fadeIn opacity-0 animation-delay-300">
                        Explorez nos articles experts sur le développement web,
                        mobile, le design et le SEO
                    </p>
                </div>

                <div className="mb-16 relative z-10 animate-fadeIn opacity-0 animation-delay-500">
                    <div className="max-w-4xl mx-auto">
                        <div className="mb-8">
                            <SearchBar onSearch={handleSearch} />
                        </div>

                        <div className="flex justify-center">
                            <CategoryFilter
                                categories={categories}
                                selectedCategory={selectedCategory}
                                onCategoryChange={handleFilterChange}
                            />
                        </div>
                    </div>
                </div>

                {/* Conteneur avec positionnement fixe pour éviter tout décalage */}
                <div
                    className="w-full relative animate-fadeIn opacity-0 animation-delay-700"
                    style={{ minHeight: "600px" }}
                >
                    {filteredArticles.length === 0 ? (
                        <div className="text-center py-16 bg-white rounded-2xl shadow-sm animate-fadeIn">
                            <svg
                                className="w-12 h-12 mx-auto text-gray-300 mb-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                                />
                            </svg>
                            <h3 className="text-xl font-medium text-gray-800 mb-2">
                                Aucun article trouvé
                            </h3>
                            <p className="text-gray-500">
                                Essayez d'ajuster vos critères de recherche
                            </p>
                        </div>
                    ) : (
                        <ArticleContainer
                            articles={filteredArticles}
                            isNewArticle={isNewArticle}
                        />
                    )}
                </div>

                <div className="mt-16 text-center animate-fadeIn opacity-0 animation-delay-900">
                    <span className="text-gray-400 text-sm">
                        Affichage de {filteredArticles.length} articles sur{" "}
                        {articles.length} au total
                    </span>
                </div>
            </div>
        </main>
    );
};

export default Blog;
